import { remPx, regexMatch } from "@/Utility"
import { KeyFrame, KeyFrameEase } from "./KeyFrame"
import { Synchroniser } from "@/Synchroniser"
import { Power2, Power0, Power1, Elastic } from "gsap";
import { gsap } from "gsap";
import TextPlugin from "gsap/TextPlugin";
import {SlideElementType} from "@/Core/SlideshowData";


gsap.registerPlugin(TextPlugin);

export class SlideTimeline {
    private _gsapTimeline: TimelineMax;
    private _elementTimelines: ElementTimeline[];

    constructor(private sync: Synchroniser, elementTimelines: ElementTimeline[]) {
        this.elementTimelines = elementTimelines;
    }

    get elementTimelines() {
        return this._elementTimelines;
    }

    set elementTimelines(ets: ElementTimeline[]) {
        this._elementTimelines = ets;
        this.reload();
    }

    setPlaybackSpeed(speed: number) {
        this._gsapTimeline.timeScale(speed);
    }

    play() {
        this._gsapTimeline.timeScale(this.sync.playbackSpeed);
        this._gsapTimeline.play();
    }

    pause() {
        this._gsapTimeline.pause();
    }

    seek(time: number) {
        this._gsapTimeline.seek(time, false);
    }

    reload() {
        let t = 0;

        if (this._gsapTimeline != null) {
            t = this._gsapTimeline.time();
            this._gsapTimeline.kill();
        }

        let rem = remPx();
        this._gsapTimeline = gsap.timeline();

        for (let et of this._elementTimelines) {
            let tweens = createTweensFromElementTimeline(et, rem);
            for (let tween of tweens) {
                this._gsapTimeline.add(tween.tween, tween.start);
            }
        }

        if (!this.sync.isPlaying)
            this._gsapTimeline.pause();

        this._gsapTimeline.seek(t, false);
    }

    destroy() {
        if (this._gsapTimeline) {
            this._gsapTimeline.kill();
        }
    }
}

export interface ElementTimeline {
    type: SlideElementType;
    element: HTMLElement;
    keyFrames: KeyFrame[];
    origin: string;
}

interface KeyFrameTween {
    tween: TweenMax;
    start: number;
    end: number;
    length: number;
}

let interceptions: {[key: string]: ((val: any, remPx: number) => any)} = {
    "x": (value: number, remPx: number) => { return value * remPx; },
    "y": (value: number, remPx: number) => { return value * remPx; },
    "clip": (value: string, remPx: number) => {
        let numberRegex = /^[ ]*([0-9]*.?[0-9]+)/;
        let unitRegex = /^[a-z]+/;
        let pos = 5;
        let result = "rect(";

        for (; ;) {
            let numberMatch = regexMatch(numberRegex, value, pos);
            if (numberMatch === null) break;
            pos = pos + numberMatch.length;

            let unit = regexMatch(unitRegex, value, pos);
            if (unit === null) {
                unit = "";
            }

            pos = pos + unit.length;
            let n = parseFloat(numberMatch);

            if (unit === "rem") {
                n *= remPx;
                unit = "px";
            }

            result += n + unit + " ";
        }

        return result + ")"
    }
};

function getFullPropName(name: keyof KeyFrame): string {
    switch (name) {
        case "c": return "color";
        case "o": return "opacity";
        case "r": return "rotation";
        case "str": return "stroke";
        case "sc": return "scale";
        case "sx": return "scaleX";
        case "sy": return "scaleY";
        case "t": return "text";
        case "z": return "zIndex";
        default: return name;
    }
}

function createTweensFromElementTimeline(et: ElementTimeline, rem: number): KeyFrameTween[] {
    let lastFrameTime = 0;
    let result: KeyFrameTween[] = [];
    let ease = Power2.easeOut;

    for (let frame of et.keyFrames) {
        let t: any = {};

        let prop: keyof KeyFrame;
        for (prop in frame) {
            if(prop.indexOf("canCopy")>-1) continue;
            let value = frame[prop];
            if (value == null) continue;
            if (prop === "s" || prop === "e")
                continue;

            let fullPropName = getFullPropName(prop);
            let valueTransformer = interceptions[fullPropName];
            if (valueTransformer) {
                value = valueTransformer(value, rem);
            }
            t[fullPropName] = value;
        }
        
        if (frame.e) {
            switch (frame.e) {
                case KeyFrameEase.Linear:
                    ease = Power0.easeNone;
                    break;
                case KeyFrameEase.Power1EaseIn:
                    ease = Power1.easeIn;
                    break;
                case KeyFrameEase.Power1EaseOut:
                    ease = Power1.easeOut;
                    break;
                case KeyFrameEase.Power1EaseInOut:
                    ease = Power1.easeInOut;
                    break;
                case KeyFrameEase.Power2EaseIn:
                    ease = Power2.easeIn;
                    break;
                case KeyFrameEase.Power2EaseInOut:
                    ease = Power2.easeInOut;
                    break;
                case KeyFrameEase.Power2EaseOut:
                    ease = Power2.easeOut;
                    break;
                case KeyFrameEase.ElasticEaseIn:
                    ease = Elastic.easeIn;
                    break;
                case KeyFrameEase.ElasticEaseInOut:
                    ease = Elastic.easeOut;
                    break;
                case KeyFrameEase.ElasticEaseOut:
                    ease = Elastic.easeOut;
                    break;
                default:
                    break;
            }
        }

        t.ease = ease;

        let tween = gsap.to(et.element, frame.s - lastFrameTime, t);
        result.push({
            tween: tween,
            start: lastFrameTime,
            end: frame.s,
            length: frame.s - lastFrameTime
        });

        tween.eventCallback("onStart", () => {
            if (et.element.style.opacity == "0"){
                et.element.style.pointerEvents = "none";
            }
            else {
                et.element.style.pointerEvents = "auto";
            }
        });

        tween.eventCallback("onComplete", () => {
            if (et.element.style.opacity == "0"){
                et.element.style.pointerEvents = "none";
            }
            else {
                et.element.style.pointerEvents = "auto";
            }
        });

        lastFrameTime = frame.s;
    }

    return result;
}
